import { getBirthDateString, sanitizePhoneNumber, } from "../../../customer/index.js";
import { SOURCE_VALUES } from "../../../subscription-contract/index.js";
import { convertDateToRenewDateTime } from "../../../subscription-contract/utils/convertDateToRenewDateTime.js";
import { getRenewAtWithPause } from "../getRenewAtWithPause.js";
import { toOptionalString } from "./toOptionalString.js";
const normalizeAddressLine = (line) => line
    .trim()
    .replace(/\s+/g, " ")
    .replace(/[,\.;]+$/g, "")
    .toLowerCase();
const combineAddressLines = (line1, line2) => {
    const l1 = (line1 ?? "").trim();
    const l2 = (line2 ?? "").trim();
    if (!l2)
        return l1;
    if (!l1)
        return l2;
    const n1 = normalizeAddressLine(l1);
    const n2 = normalizeAddressLine(l2);
    // If normalized lines match, return first, else combine them
    return n1 === n2 ? l1 : `${l1}, ${l2}`;
};
const normalizeHeaderKey = (key) => key
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "")
    .replace(/[_\-.]/g, "");
const cellToString = (value) => {
    if (value === null || value === undefined)
        return "";
    if (value instanceof Date) {
        // ISO date (YYYY-MM-DD)
        const yyyy = value.getFullYear();
        const mm = String(value.getMonth() + 1).padStart(2, "0");
        const dd = String(value.getDate()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd}`;
    }
    if (typeof value === "boolean")
        return value ? "true" : "false";
    if (typeof value === "number") {
        // Ensure we don't return scientific notation
        if (!Number.isFinite(value))
            return "";
        // If the number is effectively an integer, return it without decimal places
        const asInt = Math.trunc(value);
        if (Math.abs(value - asInt) < 1e-9)
            return String(asInt);
        return String(value);
    }
    return String(value).trim();
};
const toOptionalTrimmed = (value) => {
    const string = cellToString(value).trim();
    return string ? string : undefined;
};
const toZipCode4 = (value) => {
    const string = cellToString(value).trim();
    if (!string)
        return "";
    const digits = string.replace(/\D+/g, "");
    if (!digits)
        return "";
    return digits.padStart(4, "0").slice(0, 4);
};
export const mapSaltoDataToContractFormat = (saltoData, sku) => {
    return saltoData.map((result) => {
        const delivery_name = (result.LeveringNavn ?? "").trim();
        const billing_name = (result.InvoiceAdressToName ?? "").trim();
        const delivery_first = delivery_name.split(/\s+/).slice(0, -1).join(" ") || "";
        const delivery_last = delivery_name.split(/\s+/).slice(-1).join("") || "";
        const billing_first = billing_name.split(/\s+/).slice(0, -1).join(" ") || "";
        const billing_last = billing_name.split(/\s+/).slice(-1).join("") || "";
        const raw_birth_date = toOptionalString(result.Fødselsdato);
        const birth_date = raw_birth_date
            ? getBirthDateString(raw_birth_date)
            : undefined;
        const customerEmail = toOptionalString(result["E-post"]);
        const salto_created_at = toOptionalString(result.Innmeldingsdato)
            ? convertDateToRenewDateTime(result.Innmeldingsdato)
            : "";
        const pause_start = toOptionalString(result["Pause-Start"])
            ? convertDateToRenewDateTime(result["Pause-Start"])
            : undefined;
        const pause_stop = toOptionalString(result["Pause-Stopp"])
            ? convertDateToRenewDateTime(result["Pause-Stopp"])
            : undefined;
        const renew_at = getRenewAtWithPause(toOptionalString(result.NesteKjøring), toOptionalString(result["Pause-Start"]), toOptionalString(result["Pause-Stopp"]));
        const rawSaltoDebtStatus = toOptionalString(result.inkasso_status);
        let salto_debt_status;
        if (rawSaltoDebtStatus) {
            if (rawSaltoDebtStatus === "1") {
                salto_debt_status = "ACTIVE";
            }
            else {
                salto_debt_status = rawSaltoDebtStatus;
            }
        }
        return {
            first_name: result.fornavn?.trim() || "",
            last_name: result.etternavn?.trim() || "",
            birth_date,
            phone_number: result.Mobilnummer && result.Mobilnummer.trim()
                ? sanitizePhoneNumber(result.Mobilnummer.trim())
                : undefined,
            email: customerEmail,
            delivery_first_name: delivery_first,
            delivery_last_name: delivery_last,
            delivery_address: combineAddressLines(result.leveringAdresse1, result.leveringAdresse2),
            delivery_zip_code: result.leveringPostnummer?.trim() || "",
            delivery_city: result.leveringPoststed?.trim() || "",
            billing_first_name: billing_first,
            billing_last_name: billing_last,
            billing_address: combineAddressLines(result.InvoiceAddressLine1, result.InvoiceAddressLine2),
            billing_zip_code: result.InvoicePostnummer?.trim() || "",
            billing_city: result.InvoicePoststed?.trim() || "",
            next_isbn: result.NesteISBN?.trim() || "",
            sku: sku?.trim() || "",
            source: SOURCE_VALUES.SALTO,
            salto_customer_id: result["Kunde-ID"]?.trim() || "",
            salto_subscription_id: result.SaltoMedlemsnummer?.trim() || "",
            salto_customer_florida_id: toOptionalString(result.FloridaId),
            salto_debt_status,
            dds_id: toOptionalString(result.ddsId),
            data_factory_id: toOptionalString(result.DatafactoryId),
            salto_created_at,
            renew_at,
            pause_start,
            pause_stop,
            interval: result.Distribusjonsintervall
                ? Number(result.Distribusjonsintervall)
                : undefined,
            kindle_email: toOptionalString(result.email)?.toLowerCase(),
            verified_email: (result.Kundestatus ?? "").trim().toUpperCase() === "PARTY_ENABLED" &&
                customerEmail
                ? customerEmail
                : undefined,
        };
    });
};
export function mapTmDataToContractFormat(rows) {
    return rows.map((row) => {
        const entries = Object.entries(row);
        const map = new Map(entries.map(([key, value]) => [normalizeHeaderKey(key), value]));
        const getAny = (...keys) => {
            for (const key of keys) {
                const value = map.get(normalizeHeaderKey(key));
                if (value !== undefined)
                    return value;
            }
            return undefined;
        };
        const first_name = cellToString(getAny("Fornavn", "first_name")).trim();
        const last_name = cellToString(getAny("Etternavn", "last_name")).trim();
        const billing_address = cellToString(getAny("FakturaAdresselinje1", "billing_address_line1")).trim();
        const billing_zip_code = toZipCode4(getAny("FakturaPostnummer", "billing_zip_code"));
        const billing_city = cellToString(getAny("FakturaPoststed", "billing_city")).trim();
        const raw_birth = toOptionalTrimmed(getAny("Fodselsdato", "Fødselsdato", "birth_date"));
        const birth_date = raw_birth ? getBirthDateString(raw_birth) : undefined;
        const phoneRaw = toOptionalTrimmed(getAny("Mobilnummer", "phone_number", "phone"));
        const phone_number = phoneRaw ? sanitizePhoneNumber(phoneRaw) : undefined;
        const email = toOptionalTrimmed(getAny("Epost", "E-post", "email"));
        const next_isbn = cellToString(getAny("NesteISBN", "next_isbn")).trim();
        const sku = cellToString(getAny("Kampanjekode", "SKU", "sku")).trim();
        // TM does not have separate billing/delivery fields, so we use the billing fields with the customer name for both
        return {
            phone_number,
            first_name: first_name || "",
            last_name: last_name || "",
            birth_date,
            email,
            billing_first_name: first_name || "",
            billing_last_name: last_name || "",
            billing_address: billing_address || "",
            billing_zip_code: billing_zip_code || "",
            billing_city: billing_city || "",
            delivery_first_name: first_name || "",
            delivery_last_name: last_name || "",
            delivery_address: billing_address || "",
            delivery_zip_code: billing_zip_code || "",
            delivery_city: billing_city || "",
            next_isbn: next_isbn || "",
            sku: sku || "",
            source: SOURCE_VALUES.TM,
        };
    });
}
